import { useCallback, useMemo, useState } from 'react';
import { FiLock, FiCopy } from 'react-icons/fi';
import type { Message } from '@/lib/types';

function formatMessageTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isYesterday) {
    return (
      'Yesterday ' +
      date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    );
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

type TextSegment = {
  text: string;
  isLink: boolean;
};

function splitMessageIntoSegments(content: string): TextSegment[] {
  const linkRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  let match = linkRegex.exec(content);

  while (match) {
    const matchStart = match.index;
    const rawMatch = match[0];

    if (matchStart > lastIndex) {
      segments.push({
        text: content.slice(lastIndex, matchStart),
        isLink: false,
      });
    }

    const trailingPunctuationMatch = rawMatch.match(/[),.!?]+$/);
    const trailingPunctuation = trailingPunctuationMatch?.[0] ?? '';
    const linkText = trailingPunctuation
      ? rawMatch.slice(0, -trailingPunctuation.length)
      : rawMatch;

    if (linkText) {
      segments.push({ text: linkText, isLink: true });
    }
    if (trailingPunctuation) {
      segments.push({ text: trailingPunctuation, isLink: false });
    }

    lastIndex = matchStart + rawMatch.length;
    match = linkRegex.exec(content);
  }

  if (lastIndex < content.length) {
    segments.push({ text: content.slice(lastIndex), isLink: false });
  }

  return segments;
}

type MessageBubbleProps = {
  message: Message;
  currentUserId: number;
  onImagePress?: (imageUrl: string) => void;
};

export function MessageBubble({
  message,
  currentUserId,
  onImagePress,
}: MessageBubbleProps) {
  const isSent = message.sender_user_id === currentUserId;
  const attachments = message.attachments || [];
  const imageAttachments = attachments.filter((att) =>
    att.attachment_type.startsWith('image/')
  );
  const videoAttachments = attachments.filter((att) =>
    att.attachment_type.startsWith('video/')
  );
  const messageText = message.content?.trim() ?? '';
  const textSegments = useMemo(
    () => splitMessageIntoSegments(message.content ?? ''),
    [message.content]
  );
  const [copied, setCopied] = useState(false);

  const handleCopyMessage = useCallback(async () => {
    if (!messageText) return;
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [messageText]);

  const handleOpenLink = useCallback((linkText: string) => {
    const normalizedUrl = /^https?:\/\//i.test(linkText)
      ? linkText
      : `https://${linkText}`;
    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <div className={`flex my-1 group ${isSent ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl relative ${
          isSent
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-white border border-gray-200 rounded-bl-sm'
        }`}
      >
        {!isSent && message.sender_first_name && (
          <p className='text-xs font-semibold text-primary mb-1'>
            {message.sender_first_name}
          </p>
        )}

        {imageAttachments.length > 0 && (
          <div className='space-y-2 mb-2'>
            {imageAttachments.map((attachment) => (
              <button
                key={attachment.id}
                type='button'
                onClick={() => onImagePress?.(attachment.download_url)}
                className='block focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-xl overflow-hidden'
              >
                <img
                  src={attachment.download_url}
                  alt={attachment.name}
                  className='w-[200px] h-[200px] object-cover rounded-xl'
                />
              </button>
            ))}
          </div>
        )}

        {videoAttachments.length > 0 && (
          <div className='space-y-2 mb-2'>
            {videoAttachments.map((attachment) => (
              <video
                key={attachment.id}
                src={attachment.download_url}
                controls
                playsInline
                preload='metadata'
                className='max-w-[240px] rounded-xl'
              />
            ))}
          </div>
        )}

        {message.content && (
          <p
            className={`text-[15px] leading-5 ${
              isSent ? 'text-white' : 'text-gray-900'
            }`}
          >
            {textSegments.map((seg, i) =>
              seg.isLink ? (
                <button
                  key={i}
                  type='button'
                  onClick={() => handleOpenLink(seg.text)}
                  className={`underline break-all ${
                    isSent ? 'text-white' : 'text-primary'
                  }`}
                >
                  {seg.text}
                </button>
              ) : (
                <span key={i}>{seg.text}</span>
              )
            )}
          </p>
        )}

        <div className='flex items-center gap-1 mt-1'>
          <span
            className={`text-[11px] ${
              isSent ? 'text-white/70' : 'text-gray-400'
            }`}
          >
            {formatMessageTime(message.timestamp)}
          </span>
          <FiLock
            className={`w-2.5 h-2.5 ${
              isSent ? 'text-white/70' : 'text-gray-400'
            }`}
          />
          {messageText ? (
            <button
              type='button'
              onClick={() => void handleCopyMessage()}
              title='Copy message'
              className={`ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                isSent ? 'text-white/70 hover:text-white' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <FiCopy className='w-3 h-3' />
            </button>
          ) : null}
          {copied ? (
            <span className={`text-[10px] ${isSent ? 'text-white/70' : 'text-gray-400'}`}>
              Copied
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
